"use client";

import type React from "react";
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";

interface TokenDetailEntry {
	[action: string]: any;
	lastFetch: Date;
}

interface TokenDetailData {
	[tokenId: string]: TokenDetailEntry;
}

interface GlobalDataState {
	// Token details cache
	tokenDetailsCache: TokenDetailData;

	// Referral code
	referralCode: string;

	// Methods
	getTokenDetails: (
		tokenId: string,
		action: string,
		options?: any,
	) => Promise<any>;
	refreshTokenDetails: (tokenId: string) => Promise<void>;
	setReferralCode: (code: string) => void;
}

const GlobalDataContext = createContext<GlobalDataState | undefined>(undefined);

// Cache configuration
const TOKEN_DETAIL_CACHE_TIME = 3000; // 3 seconds
const DEFAULT_REFERRAL_CODE = "yKYSEREM";
const REFERRAL_CODE_STORAGE_KEY = "tyche_referral_code";

export function GlobalDataProvider({
	children,
}: {
	children: React.ReactNode;
}) {
	// Token details cache
	const [tokenDetailsCache, setTokenDetailsCache] = useState<TokenDetailData>(
		{},
	);

	// Referral code state
	const [referralCode, setReferralCodeState] = useState<string>(
		DEFAULT_REFERRAL_CODE,
	);

	// Token detail requests management
	const tokenDetailRequests = useRef<Map<string, Promise<any>>>(new Map());

	// Parse referral code from URL and localStorage on mount
	useEffect(() => {
		// First, check localStorage for saved referral code
		const savedReferralCode = localStorage.getItem(REFERRAL_CODE_STORAGE_KEY);

		// Then, check URL parameters (URL params take precedence)
		if (typeof window !== "undefined") {
			const urlParams = new URLSearchParams(window.location.search);
			const refParam = urlParams.get("ref");

			if (refParam) {
				// URL has ref parameter, use it and save to localStorage
				setReferralCodeState(refParam);
				localStorage.setItem(REFERRAL_CODE_STORAGE_KEY, refParam);
			} else if (savedReferralCode) {
				// No URL param, but we have a saved code
				setReferralCodeState(savedReferralCode);
			} else {
				// No URL param or saved code, use default and save it
				setReferralCodeState(DEFAULT_REFERRAL_CODE);
				localStorage.setItem(REFERRAL_CODE_STORAGE_KEY, DEFAULT_REFERRAL_CODE);
			}
		}
	}, []);

	// Method to update referral code
	const setReferralCode = useCallback((code: string) => {
		setReferralCodeState(code);
		localStorage.setItem(REFERRAL_CODE_STORAGE_KEY, code);
	}, []);

	// Get token details with caching
	const getTokenDetails = useCallback(
		async (tokenId: string, action: string, options: any = {}) => {
			const cacheKey = `${tokenId}-${action}`;
			const cached = tokenDetailsCache[tokenId];

			// Check if we have valid cached data
			if (cached && cached[action] && cached.lastFetch) {
				const cacheAge = Date.now() - cached.lastFetch.getTime();
				if (cacheAge < TOKEN_DETAIL_CACHE_TIME) {
					return cached[action];
				}
			}

			// Check if request is already in progress
			const existingRequest = tokenDetailRequests.current.get(cacheKey);
			if (existingRequest) {
				return existingRequest;
			}

			// Make new request
			const requestPromise = (async () => {
				try {
					const response = await fetch(`/api/tokens/${tokenId}`, {
						method: "POST",
						headers: {
							"Content-Type": "application/json",
						},
						body: JSON.stringify({
							action,
							...options,
						}),
					});

					const result = await response.json();

					if (result.success) {
						// Update cache
						setTokenDetailsCache((prev) => ({
							...prev,
							[tokenId]: {
								...prev[tokenId],
								[action]: result,
								lastFetch: new Date(),
							},
						}));

						return result;
					} else {
						throw new Error(result.error || "Failed to fetch token details");
					}
				} finally {
					tokenDetailRequests.current.delete(cacheKey);
				}
			})();

			tokenDetailRequests.current.set(cacheKey, requestPromise);
			return requestPromise;
		},
		[tokenDetailsCache],
	);

	// Refresh token details
	const refreshTokenDetails = useCallback(async (tokenId: string) => {
		// Clear cache for this token
		setTokenDetailsCache((prev) => {
			const newCache = { ...prev };
			delete newCache[tokenId];
			return newCache;
		});
	}, []);

	const value: GlobalDataState = {
		tokenDetailsCache,
		referralCode,
		getTokenDetails,
		refreshTokenDetails,
		setReferralCode,
	};

	return (
		<GlobalDataContext.Provider value={value}>
			{children}
		</GlobalDataContext.Provider>
	);
}

// Custom hook to use global data
export function useGlobalData() {
	const context = useContext(GlobalDataContext);
	if (context === undefined) {
		throw new Error("useGlobalData must be used within a GlobalDataProvider");
	}
	return context;
}

// Specialized hook for token details

export function useTokenDetails(tokenId: string) {
	const { getTokenDetails, refreshTokenDetails, tokenDetailsCache } =
		useGlobalData();
	const cached = tokenDetailsCache[tokenId];

	return {
		getTokenDetails: (action: string, options?: any) =>
			getTokenDetails(tokenId, action, options),
		refreshToken: () => refreshTokenDetails(tokenId),
		cached,
	};
}

// Hook for referral code
export function useReferralCode() {
	const { referralCode, setReferralCode } = useGlobalData();

	return {
		referralCode,
		setReferralCode,
		defaultCode: DEFAULT_REFERRAL_CODE,
	};
}
